// Resizing Service Functionality
class ResizingService {
  constructor() {
    this.init();
  }

  init() {
    console.log('ResizingService initialized');
    // Wait for DOM to be fully loaded before binding events
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.bindEvents();
        this.initCartMonitoring();
      });
    } else {
      this.bindEvents();
      this.initCartMonitoring();
    }
  }

  bindEvents() {
    // Handle checkbox toggle
    const checkbox = document.querySelector('.resizing-checkbox');
    if (checkbox) {
      checkbox.addEventListener('change', (e) => {
        this.toggleInputField(e.target);
      });
    }

    // Handle done button click
    const doneButton = document.querySelector('.resizing-done-button');
    if (doneButton) {
      doneButton.addEventListener('click', (e) => {
        this.handleDoneClick(e.target);
      });
    }

    // Intercept main product form submission with a slight delay
    setTimeout(() => {
      this.interceptProductFormSubmission();
    }, 100);
  }

  initCartMonitoring() {
    // Listen for cart updates
    document.addEventListener('cart:refresh', () => {
      this.checkForRemovedMainProducts();
    });
    
    // Listen for item removals
    document.addEventListener('click', (e) => {
      if (e.target.matches('.cart-remove, .cart-remove *, [data-cart-remove], [data-cart-remove] *')) {
        setTimeout(() => {
          this.checkForRemovedMainProducts();
        }, 500); // Small delay to ensure cart is updated
      }
    });
  }

  async checkForRemovedMainProducts() {
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Get all resizing products in cart
      const resizingItems = cart.items.filter(item => 
        item.properties && item.properties._resizing_service === 'true'
      );
      
      // Check if their associated main products still exist
      for (const resizingItem of resizingItems) {
        const mainVariantId = resizingItem.properties._main_product_variant_id;
        if (mainVariantId) {
          const mainProductExists = cart.items.some(item => 
            item.variant_id.toString() === mainVariantId.toString() && 
            (!item.properties || item.properties._resizing_service !== 'true')
          );
          
          if (!mainProductExists) {
            // Remove the resizing product
            await this.removeResizingProduct(resizingItem.key);
          }
        }
      }
    } catch (error) {
      console.error('Error checking for removed main products:', error);
    }
  }

  async removeResizingProduct(itemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      
      if (response.ok) {
        console.log('Resizing product removed automatically');
        
        // Get updated cart data
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        
        // Trigger comprehensive cart refresh events
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        
        // Force mini-cart/drawer refresh if available
        const miniCart = document.querySelector('mini-cart, cart-drawer, .mini-cart');
        if (miniCart && typeof miniCart.refresh === 'function') {
          miniCart.refresh();
        }
        
        // Trigger theme-specific cart update events
        if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
          window.theme.cart.refresh();
        }
        
        // Force page reload as fallback if cart UI doesn't update
        setTimeout(() => {
          const resizingItemsStillVisible = document.querySelectorAll('[data-cart-item] .resizing-service, [data-line-item] .resizing-service');
          if (resizingItemsStillVisible.length > 0) {
            console.log('Cart UI not updated, forcing page reload');
            window.location.reload();
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error removing resizing product:', error);
    }
  }

  toggleInputField(checkbox) {
    const inputWrapper = document.querySelector('.resizing-input-wrapper');
    const input = document.querySelector('.resizing-input');
    
    if (inputWrapper) {
      if (checkbox.checked) {
        inputWrapper.style.display = 'flex';
      } else {
        inputWrapper.style.display = 'none';
        // Clear input when hiding
        if (input) input.value = '';
      }
    }
  }

  handleDoneClick(button) {
    const input = document.querySelector('.resizing-input');
    const checkbox = document.querySelector('.resizing-checkbox');
    
    // Validation
    if (!checkbox || !checkbox.checked) {
      this.showError('Please check the resizing option first.');
      return;
    }

    if (!input || !input.value.trim()) {
      this.showError('Please enter resizing details.');
      if (input) input.focus();
      return;
    }

    // Save the details (just hide the input and show success)
    this.saveResizingDetails(input.value.trim());
    this.showSuccess('Resizing details saved! Add the main product to cart to include resizing service.');
    
    // Hide the input field but keep checkbox checked
    const inputWrapper = document.querySelector('.resizing-input-wrapper');
    if (inputWrapper) {
      inputWrapper.style.display = 'none';
    }
  }

  saveResizingDetails(details) {
    // Store the resizing details for later use
    this.resizingDetails = details;
  }

  interceptProductFormSubmission() {
    // Find the product form using multiple selectors
    const productForm = document.querySelector('form[is="product-form"]') || 
                       document.querySelector('form[action*="/cart/add"]') ||
                       document.querySelector('product-form');
    
    if (!productForm) {
      console.log('ResizingService: Product form not found');
      return;
    }

    console.log('ResizingService: Found product form:', productForm);

    // Add event listener to intercept form submission
    productForm.addEventListener('submit', async (e) => {
      const checkbox = document.querySelector('.resizing-checkbox');
      
      console.log('ResizingService: Form submitted, checkbox checked:', checkbox?.checked);
      
      // If resizing checkbox is checked, handle resizing service
      if (checkbox && checkbox.checked) {
        console.log('ResizingService: Intercepting form submission');
        e.preventDefault();
        await this.handleMainProductWithResizing(productForm);
      }
    });
  }

  async handleMainProductWithResizing(productForm) {
    console.log('ResizingService: handleMainProductWithResizing called');
    
    const input = document.querySelector('.resizing-input');
    const checkbox = document.querySelector('.resizing-checkbox');
    
    console.log('ResizingService: resizingDetails:', this.resizingDetails);
    console.log('ResizingService: input value:', input?.value);
    
    // Validation: Check if details are entered when checkbox is checked
    if (!this.resizingDetails && (!input || !input.value.trim())) {
      console.log('ResizingService: Validation failed - no details');
      this.showError('Please enter resizing details or uncheck the resizing option.');
      // Show input field if hidden
      const inputWrapper = document.querySelector('.resizing-input-wrapper');
      if (inputWrapper) {
        inputWrapper.style.display = 'block';
      }
      if (input) input.focus();
      return;
    }

    // Use saved details or current input value
    const resizingDetails = this.resizingDetails || input.value.trim();
    console.log('ResizingService: Using details:', resizingDetails);
    
    try {
      console.log('ResizingService: Adding main product to cart');
      // First add the main product to cart
      await this.addMainProductToCart(productForm);
      
      console.log('ResizingService: Adding resizing service to cart');
      // Then add the resizing service
      const variantInfo = this.getCurrentVariant();
      await this.addResizingToCart(null, resizingDetails, variantInfo);
      
    } catch (error) {
      console.error('ResizingService: Error adding products to cart:', error);
      this.showError('Failed to add products to cart. Please try again.');
    }
  }

  async addMainProductToCart(productForm) {
    console.log('ResizingService: addMainProductToCart called with form:', productForm);
    
    const formData = new FormData(productForm);
    formData.append('sections', ['mini-cart']);
    
    console.log('ResizingService: FormData prepared, making request to /cart/add.js');
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    console.log('ResizingService: Main product response status:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('ResizingService: Main product add failed:', errorData);
      throw new Error(errorData.description || 'Failed to add main product to cart');
    }
    
    const result = await response.json();
    console.log('ResizingService: Main product added successfully:', result);
    return result;
  }

  getCurrentVariant() {
    // First try to get variant from the product variants component (theme.js)
    const productVariants = document.querySelector('product-variants');
    if (productVariants && productVariants.selectedVariant) {
      const variant = productVariants.selectedVariant;
      const variantOptions = variant.options ? variant.options.join(' / ') : 'Default';
      return {
        id: variant.id,
        title: this.getProductTitle(),
        option: variantOptions
      };
    }
    
    // Check URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      // Get variant details from the page
      const variantSelector = document.querySelector(`[data-variant-id="${variantId}"]`);
      if (variantSelector) {
        return {
          id: variantId,
          title: variantSelector.dataset.variantTitle || '',
          option: variantSelector.dataset.variantOption || ''
        };
      }
    }

    // Fallback to currently selected variant or first variant
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        const selectedVariantId = variantInput.value;
        const variantOption = this.getVariantOptionName(selectedVariantId);
        return {
          id: selectedVariantId,
          title: this.getProductTitle(),
          option: variantOption
        };
      }
    }

    return {
      id: null,
      title: this.getProductTitle(),
      option: 'Default'
    };
  }

  getVariantOptionName(variantId) {
    // Try to get variant option from variant picker
    const variantPicker = document.querySelector('.variant-picker');
    if (variantPicker) {
      const selectedOptions = variantPicker.querySelectorAll('input:checked, select option:selected');
      const options = Array.from(selectedOptions).map(option => {
        return option.value || option.textContent.trim();
      }).filter(Boolean);
      return options.join(' / ') || 'Default';
    }
    
    // Try to get from product form variant selector
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantSelectors = productForm.querySelectorAll('select[name*="option"], input[name*="option"]:checked');
      const options = Array.from(variantSelectors).map(selector => {
        if (selector.tagName === 'SELECT') {
          return selector.options[selector.selectedIndex]?.text || selector.value;
        } else {
          return selector.value || selector.getAttribute('data-value');
        }
      }).filter(Boolean);
      
      if (options.length > 0) {
        return options.join(' / ');
      }
    }
    
    // Try to get from variant data attributes
    const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
    if (variantElement) {
      const variantTitle = variantElement.getAttribute('data-variant-title') || 
                          variantElement.getAttribute('data-title') ||
                          variantElement.textContent.trim();
      if (variantTitle && variantTitle !== 'Default Title') {
        return variantTitle;
      }
    }
    
    return 'Default';
  }

  getProductTitle() {
    const titleElement = document.querySelector('.product__title, .product-title, h1');
    return titleElement ? titleElement.textContent.trim() : 'Product';
  }

  async checkMainProductInCart(productId) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      return cart.items.some(item => {
        return item.product_id.toString() === productId.toString();
      });
    } catch (error) {
      console.error('Error checking cart:', error);
      return false;
    }
  }

  async addResizingToCart(button, resizingText, variantInfo) {
    // Use the specific resizing variant ID
    const resizingVariantId = 53316039704908;
    
    // Show loading state if button is provided
    if (button) {
      button.classList.add('loading');
      button.disabled = true;
    }

    try {

      // Prepare cart add data
      const cartData = {
        id: resizingVariantId,
        quantity: 1,
        properties: {
          'Resizing Details': resizingText,
          'Main Product': variantInfo.title,
          'Selected Variant': variantInfo.option,
          '_resizing_service': 'true',
          '_main_product_variant_id': variantInfo.id
        }
      };

      // Add to cart via AJAX
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add resizing service');
      }

      // Success feedback
      this.showSuccess('Products added to cart successfully!');
      
      // Reset form
      this.resetForm();
      
      // Trigger cart update events like the theme does
      try {
        console.log('ResizingService: Updating cart and triggering events');
        
        // Get cart URL with fallback
        let cartUrl = '/cart';
        if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.cartUrl) {
          cartUrl = window.themeVariables.routes.cartUrl;
        } else {
          console.warn('ResizingService: themeVariables not available, using fallback cart URL');
        }
        
        const cartResponse = await fetch(`${cartUrl}.js`);
        
        if (!cartResponse.ok) {
          console.error('ResizingService: Failed to fetch cart:', cartResponse.status);
          throw new Error('Failed to update cart');
        }
        
        const cartContent = await cartResponse.json();
        console.log('ResizingService: Cart updated successfully:', cartContent);
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));
        
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === 'drawer'
          }
        }));
        
        console.log('ResizingService: Cart events dispatched successfully');
      } catch (cartError) {
        console.error('ResizingService: Cart update error:', cartError);
        // Don't throw here as the products were already added successfully
      }

    } catch (error) {
      console.error('Error adding resizing service:', error);
      this.showError(error.message || 'Failed to add resizing service. Please try again.');
    } finally {
      // Remove loading state if button is provided
      if (button) {
        button.classList.remove('loading');
        button.disabled = false;
      }
    }
  }

  resetForm() {
    const checkbox = document.querySelector('.resizing-checkbox');
    const input = document.querySelector('.resizing-input');
    const inputWrapper = document.querySelector('.resizing-input-wrapper');
    
    if (checkbox) checkbox.checked = false;
    if (input) input.value = '';
    if (inputWrapper) inputWrapper.style.display = 'none';
    
    // Clear saved resizing details
    this.resizingDetails = null;
  }

  showError(message) {
    this.showNotification(message, 'error');
  }

  showSuccess(message) {
    this.showNotification(message, 'success');
  }

  showNotification(message, type) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `resizing-notification resizing-notification--${type}`;
    notification.textContent = message;
    
    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '4px',
      color: 'white',
      fontWeight: '500',
      zIndex: '9999',
      maxWidth: '300px',
      backgroundColor: type === 'error' ? '#dc3545' : '#28a745',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    // Remove after 4 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 4000);
  }
}

// Initialize the resizing service
new ResizingService();